/**
 * Event types that can be tracked across the application
 */
export enum EventType {
  PAGE_VIEW = "page_view",
  BUTTON_CLICK = "button_click",
  FORM_SUBMISSION = "form_submission",
  PRODUCT_VIEW = "product_view",
  ADD_TO_CART = "add_to_cart",
  REMOVE_FROM_CART = "remove_from_cart",
  CHECKOUT_STARTED = "checkout_started",
  CHECKOUT_COMPLETED = "checkout_completed",
  SEARCH = "search",
  FILTER_APPLIED = "filter_applied",
  NEWSLETTER_SUBSCRIPTION = "newsletter_subscription",
  USER_SIGNUP = "user_signup",
  USER_LOGIN = "user_login",
  CUSTOM = "custom",
  USER_ENGAGEMENT = "user_engagement",
  SESSION_START = "session_start",
  FIRST_VISIT = "first_visit",
  SCROLL = "scroll",
  FORM_START = "form_start",
  ADD_PAYMENT_INFO = "add_payment_info",
  ORDER_PLACED = "order_placed",
  CART_VIEW = "cart_view",
  CART_VIEWED = "cart_viewed",

  // New MoEngage events from the list
  ITEM_PURCHASED = "item_purchased",
  UPDATE_CART = "update_cart",
  CUSTOMER_REGISTERED = "customer_registered",
  CUSTOMER_LOGGED_IN = "customer_logged_in",
  PRODUCT_SEARCHED = "product_searched",
  SHOPIFY_CHECKOUT_UPDATED = "shopify_checkout_updated",
  SHOPIFY_ABANDONED_CHECKOUT = "shopify_abandoned_checkout",
  SHOPIFY_ORDER_FULFILLED = "shopify_order_fulfilled",
  SHOPIFY_CHECKOUT_STARTED = "shopify_checkout_started",
  SHOPIFY_ADD_TO_CART = "shopify_add_to_cart",
  SHOPIFY_ITEM_PURCHASED = "shopify_item_purchased",
  SHOPIFY_ORDER_PLACED = "shopify_order_placed",
  SHOPIFY_REMOVED_FROM_CART = "shopify_removed_from_cart",
  SHOPIFY_UPDATE_CART = "shopify_update_cart",
  SHOPIFY_ABANDONED_CART = "shopify_abandoned_cart",
  SHOPIFY_ORDER_CANCELED = "shopify_order_canceled",
  SHOPIFY_REFUND_CREATED = "shopify_refund_created",
  SPINFORM_RESULT = "spinform_result",
  COUPON_COPIED = "coupon_copied",
  SMASHPOPS_GAME_PLAYED = "smashpops_game_played",
  POPCOINS_CREDIT_PENDING = "popcoins_credit_pending",
  POPCOINS_SIGNUP = "popcoins_signup",
  POPCOINS_LOGIN = "popcoins_login",
  POPCOINS_CREDIT_ADDED = "popcoins_credit_added",
  POPCOINS_DEBIT_PURCHASED = "popcoins_debit_purchased",
  POPCOINS_CREDIT_CANCELED = "popcoins_credit_canceled",
  POPCOINS_CREDIT_REFUNDED = "popcoins_credit_refunded",
  STARTED_CHECKOUT_GK = "started_checkout_gk",
  MOBILE_ADDED_GK = "mobile_added_gk",
  ADDRESS_SELECTED_GK = "address_selected_gk",
  ADDRESS_COMPLETED_GK = "address_completed_gk",
  PAYMENT_METHOD_SELECTED_GK = "payment_method_selected_gk",
  MULTI_ID_USER_MERGED = "multi_id_user_merged",
  PAYMENT_COMPLETED_GK = "payment_completed_gk",
  ORDER_SUCCESS = "order_success",
  ORDER_COMPLETED = "order_completed",
  PIN_CODE_ADDED_GK = "pin_code_added_gk",
  ADDRESS_ADDED_GK = "address_added_gk",
  FLIGHT_BOOKED = "flight_booked",
  NEW_LEAD = "new_lead",
  KP_MP_PHONE_NUMBER_LOGGED_IN = "kp_mp_phone_number_logged_in",
  KP_PHONE_NUMBER_LOGGED_IN = "kp_phone_number_logged_in",
  KP_MP_SHOPIFY_LOGGED_IN = "kp_mp_shopify_logged_in",
  KP_MP_SUCCESSFULLY_LOGGED_OUT = "kp_mp_successfully_logged_out",
  KP_WHATSAPP_LOGGED_IN = "kp_whatsapp_logged_in",
  KP_MP_TRUECALLER_LOGGED_IN = "kp_mp_truecaller_logged_in",
  KP_MP_WHATSAPP_LOGGED_IN = "kp_mp_whatsapp_logged_in",
  KP_TRUECALLER_LOGGED_IN = "kp_truecaller_logged_in",
  SHOPIFY_ORDER_PARTIALLY_FULFILLED = "shopify_order_partially_fulfilled",
  KP_SHOPIFY_LOGGED_IN = "kp_shopify_logged_in",
  KP_SUCCESSFULLY_LOGGED_OUT = "kp_successfully_logged_out",
}

/**
 * Base event interface that all events should implement
 */
export interface BaseEvent {
  type: EventType;
  timestamp?: number;
}

/**
 * Page view event
 */
export interface IPageViewEvent extends BaseEvent {
  type: EventType.PAGE_VIEW;
  path: string;
  title: string;
  referrer?: string;
}

/**
 * User engagement event
 */
export interface IUserEngagementEvent extends BaseEvent {
  type: EventType.USER_ENGAGEMENT;
  engagementTime?: number; // in seconds
  pagePath?: string;
  pageTitle?: string;
  scrollDepth?: number; // in percentage
  formId?: string;
  formName?: string;
  formFields?: Array<{
    fieldName: string;
    fieldValue: string;
  }>;
  buttonId?: string;
  buttonText?: string;
  location?: string;
  customProperties?: Record<string, any>;
}

/**
 * Session start event
 */
export interface ISessionStartEvent extends BaseEvent {
  type: EventType.SESSION_START;
  sessionId: string;
  userId?: string;
  userAgent?: string;
  referrer?: string;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}

/**
 * First visit event
 */
export interface IFirstVisitEvent extends BaseEvent {
  type: EventType.FIRST_VISIT;
  userId?: string;
  userAgent?: string;
  referrer?: string;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * Scroll event
 */
export interface IScrollEvent extends BaseEvent {
  type: EventType.SCROLL;
  scrollDepth: number; // in percentage
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * Form start event
 */
export interface IFormStartEvent extends BaseEvent {
  type: EventType.FORM_START;
  formId: string;
  formName?: string;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * Form submission event
 */
export interface IFormSubmissionEvent extends BaseEvent {
  type: EventType.FORM_SUBMISSION;
  formId: string;
  formName?: string;
  success: boolean;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * User signup event
 */
export interface IUserSignupEvent extends BaseEvent {
  type: EventType.USER_SIGNUP;
  userId?: string;
  method?: string;
  success: boolean;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * User login event
 */
export interface IUserLoginEvent extends BaseEvent {
  type: EventType.USER_LOGIN;
  userId?: string;
  method?: string;
  success: boolean;
  pagePath: string;
  pageTitle: string;
  timestamp: number;
  customProperties?: Record<string, any>;
}
/**
 * Button click event
 */
/**
 * Button click event
 */
export interface IButtonClickEvent extends BaseEvent {
  type: EventType.BUTTON_CLICK;
  buttonId?: string;
  buttonText?: string;
  location: string;
}

/**
 * Form submission event
 */
export interface IFormSubmissionEvent extends BaseEvent {
  type: EventType.FORM_SUBMISSION;
  formId: string;
  formName?: string;
  success: boolean;
}

/**
 * Product view event
 */
export interface IProductViewEvent extends BaseEvent {
  type: EventType.PRODUCT_VIEW;
  productId: string;
  productName: string;
  price?: number;
  currency?: string;
  category?: string;
}

/**
 * Add to cart event
 */
export interface IAddToCartEvent extends BaseEvent {
  type: EventType.ADD_TO_CART;
  productId: string;
  productName: string;
  price: number;
  currency?: string;
  quantity?: number;
  variant?: string;
}

/**
 * Remove from cart event
 */
export interface IRemoveFromCartEvent extends BaseEvent {
  type: EventType.REMOVE_FROM_CART;
  productId: string;
  productName: string;
  price: number;
  currency?: string;
  quantity: number;
  variant?: string;
}

/**
 * Checkout started event
 */
export interface ICheckoutStartedEvent extends BaseEvent {
  type: EventType.CHECKOUT_STARTED;
  cartValue: number;
  currency?: string;
  itemCount: number;
  items: Array<{
    productId: string;
    productName: string;
    price: number;
    quantity: number;
    variant?: string;
  }>;
}

/**
 * Checkout completed event
 */
export interface ICheckoutCompletedEvent extends BaseEvent {
  type: EventType.CHECKOUT_COMPLETED;
  orderId: string;
  cartValue: number;
  currency?: string;
  itemCount: number;
  items: Array<{
    productId: string;
    productName: string;
    price: number;
    quantity: number;
    variant?: string;
  }>;
}

/**
 * Cart viewed event
 */
export interface ICartViewedEvent extends BaseEvent {
  type: EventType.CART_VIEWED;
  cartId: string;
  products: Array<{
    variantId: string;
    price: number;
    quantity: number;
  }>;
}

/**
 * Search event
 */
export interface ISearchEvent extends BaseEvent {
  type: EventType.SEARCH;
  searchTerm: string;
  content_ids: string[];
  resultsCount?: number;
}

/**
 * Filter applied event
 */
export interface IFilterAppliedEvent extends BaseEvent {
  type: EventType.FILTER_APPLIED;
  filterType: string;
  filterValue: string | number | boolean;
}

/**
 * Newsletter subscription event
 */
export interface INewsletterSubscriptionEvent extends BaseEvent {
  type: EventType.NEWSLETTER_SUBSCRIPTION;
  email: string;
  success: boolean;
}

/**
 * User signup event
 */
export interface IUserSignupEvent extends BaseEvent {
  type: EventType.USER_SIGNUP;
  userId?: string;
  method?: string;
  success: boolean;
}

/**
 * User login event
 */
export interface IUserLoginEvent extends BaseEvent {
  type: EventType.USER_LOGIN;
  userId?: string;
  method?: string;
  success: boolean;
}

/**
 * Custom event for any other tracking needs
 */
export interface ICustomEvent extends BaseEvent {
  type: EventType.CUSTOM;
  name: string;
  properties?: Record<string, any>;
}

/**
 * Add Payment Info event
 */
export interface IAddPaymentInfoEvent extends BaseEvent {
  type: EventType.ADD_PAYMENT_INFO;
  cartValue: number;
  currency?: string;
  itemCount: number;
  paymentType?: string;
  items: Array<{
    productId: string;
    productName: string;
    price: number;
    quantity: number;
    variant?: string;
  }>;
}

/**
 * Item purchased event
 */
export interface IItemPurchasedEvent extends BaseEvent {
  type: EventType.ITEM_PURCHASED;
  productId: string;
  productName: string;
  price: number;
  currency?: string;
  quantity: number;
  variant?: string;
  orderId?: string;
}

/**
 * Update cart event
 */
export interface IUpdateCartEvent extends BaseEvent {
  type: EventType.UPDATE_CART;
  productId: string;
  productName: string;
  price: number;
  currency?: string;
  quantity: number;
  variant?: string;
}

/**
 * Customer registered event
 */
export interface ICustomerRegisteredEvent extends BaseEvent {
  type: EventType.CUSTOMER_REGISTERED;
  userId: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Customer logged in event
 */
export interface ICustomerLoggedInEvent extends BaseEvent {
  type: EventType.CUSTOMER_LOGGED_IN;
  userId: string;
  email?: string;
  phone?: string;
  method?: string;
}

/**
 * Shopify checkout updated event
 */
export interface IShopifyCheckoutUpdatedEvent extends BaseEvent {
  type: EventType.SHOPIFY_CHECKOUT_UPDATED;
  checkoutId: string;
  cartValue: number;
  currency?: string;
  itemCount: number;
  items: Array<{
    productId: string;
    productName: string;
    price: number;
    quantity: number;
    variant?: string;
  }>;
}

/**
 * Shopify abandoned checkout event
 */
export interface IShopifyAbandonedCheckoutEvent extends BaseEvent {
  type: EventType.SHOPIFY_ABANDONED_CHECKOUT;
  checkoutId: string;
  cartValue: number;
  currency?: string;
  itemCount: number;
  items: Array<{
    productId: string;
    productName: string;
    price: number;
    quantity: number;
    variant?: string;
  }>;
}

/**
 * Shopify order fulfilled event
 */
export interface IShopifyOrderFulfilledEvent extends BaseEvent {
  type: EventType.SHOPIFY_ORDER_FULFILLED;
  orderId: string;
  cartValue: number;
  currency?: string;
  itemCount: number;
  items: Array<{
    productId: string;
    productName: string;
    price: number;
    quantity: number;
    variant?: string;
  }>;
}

/**
 * Shopify checkout started event
 */
export interface IShopifyCheckoutStartedEvent extends BaseEvent {
  type: EventType.SHOPIFY_CHECKOUT_STARTED;
  checkoutId: string;
  cartValue: number;
  currency?: string;
  itemCount: number;
  items: Array<{
    productId: string;
    productName: string;
    price: number;
    quantity: number;
    variant?: string;
  }>;
}

/**
 * Shopify add to cart event
 */
export interface IShopifyAddToCartEvent extends BaseEvent {
  type: EventType.SHOPIFY_ADD_TO_CART;
  productId: string;
  productName: string;
  price: number;
  currency?: string;
  quantity: number;
  variant?: string;
}

/**
 * Shopify remove from cart event
 */
export interface IShopifyRemoveFromCartEvent extends BaseEvent {
  type: EventType.SHOPIFY_REMOVED_FROM_CART;
  productId: string;
  productName: string;
  price: number;
  currency?: string;
  quantity: number;
  variant?: string;
}

/**
 * Shopify update cart event
 */
export interface IShopifyUpdateCartEvent extends BaseEvent {
  type: EventType.SHOPIFY_UPDATE_CART;
  productId: string;
  productName: string;
  price: number;
  currency?: string;
  quantity: number;
  variant?: string;
}

/**
 * Spinform result event
 */
export interface ISpinformResultEvent extends BaseEvent {
  type: EventType.SPINFORM_RESULT;
  result?: string;
}

/**
 * Coupon copied event
 */
export interface ICouponCopiedEvent extends BaseEvent {
  type: EventType.COUPON_COPIED;
  couponCode?: string;
}

/**
 * Started checkout GK event
 */
export interface IStartedCheckoutGKEvent extends BaseEvent {
  type: EventType.STARTED_CHECKOUT_GK;
  cartValue?: number;
  currency?: string;
}

/**
 * Mobile added GK event
 */
export interface IMobileAddedGKEvent extends BaseEvent {
  type: EventType.MOBILE_ADDED_GK;
  mobile?: string;
}

/**
 * Address selected GK event
 */
export interface IAddressSelectedGKEvent extends BaseEvent {
  type: EventType.ADDRESS_SELECTED_GK;
  addressId?: string;
}

/**
 * Address completed GK event
 */
export interface IAddressCompletedGKEvent extends BaseEvent {
  type: EventType.ADDRESS_COMPLETED_GK;
  addressId?: string;
}

/**
 * Payment method selected GK event
 */
export interface IPaymentMethodSelectedGKEvent extends BaseEvent {
  type: EventType.PAYMENT_METHOD_SELECTED_GK;
  paymentMethod?: string;
}

/**
 * Payment completed GK event
 */
export interface IPaymentCompletedGKEvent extends BaseEvent {
  type: EventType.PAYMENT_COMPLETED_GK;
  amount?: number;
  currency?: string;
}

/**
 * Order success event
 */
export interface IOrderSuccessEvent extends BaseEvent {
  type: EventType.ORDER_SUCCESS;
  orderId?: string;
  amount?: number;
  currency?: string;
}

/**
 * Order completed event
 */
export interface IOrderCompletedEvent extends BaseEvent {
  type: EventType.ORDER_COMPLETED;
  orderId?: string;
  amount?: number;
  currency?: string;
}

/**
 * PIN code added GK event
 */
export interface IPinCodeAddedGKEvent extends BaseEvent {
  type: EventType.PIN_CODE_ADDED_GK;
  pinCode?: string;
}

/**
 * Address added GK event
 */
export interface IAddressAddedGKEvent extends BaseEvent {
  type: EventType.ADDRESS_ADDED_GK;
  addressId?: string;
}

/**
 * KP MP phone number logged in event
 */
export interface IKPMPPhoneNumberLoggedInEvent extends BaseEvent {
  type: EventType.KP_MP_PHONE_NUMBER_LOGGED_IN;
  phone?: string;
}

/**
 * KP phone number logged in event
 */
export interface IKPPhoneNumberLoggedInEvent extends BaseEvent {
  type: EventType.KP_PHONE_NUMBER_LOGGED_IN;
  phone?: string;
}

/**
 * KP MP Shopify logged in event
 */
export interface IKPMPShopifyLoggedInEvent extends BaseEvent {
  type: EventType.KP_MP_SHOPIFY_LOGGED_IN;
  userId?: string;
}

/**
 * KP MP successfully logged out event
 */
export interface IKPMPSuccessfullyLoggedOutEvent extends BaseEvent {
  type: EventType.KP_MP_SUCCESSFULLY_LOGGED_OUT;
  userId?: string;
}

/**
 * KP WhatsApp logged in event
 */
export interface IKPWhatsAppLoggedInEvent extends BaseEvent {
  type: EventType.KP_WHATSAPP_LOGGED_IN;
  phone?: string;
}

/**
 * KP MP Truecaller logged in event
 */
export interface IKPMPTruecallerLoggedInEvent extends BaseEvent {
  type: EventType.KP_MP_TRUECALLER_LOGGED_IN;
  phone?: string;
}

/**
 * KP MP WhatsApp logged in event
 */
export interface IKPMPWhatsAppLoggedInEvent extends BaseEvent {
  type: EventType.KP_MP_WHATSAPP_LOGGED_IN;
  phone?: string;
}

/**
 * KP Truecaller logged in event
 */
export interface IKPTruecallerLoggedInEvent extends BaseEvent {
  type: EventType.KP_TRUECALLER_LOGGED_IN;
  phone?: string;
}

/**
 * KP Shopify logged in event
 */
export interface IKPShopifyLoggedInEvent extends BaseEvent {
  type: EventType.KP_SHOPIFY_LOGGED_IN;
  userId?: string;
}

/**
 * KP successfully logged out event
 */
export interface IKPSuccessfullyLoggedOutEvent extends BaseEvent {
  type: EventType.KP_SUCCESSFULLY_LOGGED_OUT;
  userId?: string;
}

/**
 * Union type of all possible events
 */
export type TEvent =
  | IPageViewEvent
  | IButtonClickEvent
  | IFormSubmissionEvent
  | IProductViewEvent
  | IAddToCartEvent
  | IRemoveFromCartEvent
  | ICheckoutStartedEvent
  | ICheckoutCompletedEvent
  | ICartViewedEvent
  | ISearchEvent
  | IFilterAppliedEvent
  | INewsletterSubscriptionEvent
  | IUserSignupEvent
  | IUserLoginEvent
  | ICustomEvent
  | IUserEngagementEvent
  | ISessionStartEvent
  | IFirstVisitEvent
  | IScrollEvent
  | IFormStartEvent
  | IAddPaymentInfoEvent
  | IItemPurchasedEvent
  | IUpdateCartEvent
  | ICustomerRegisteredEvent
  | ICustomerLoggedInEvent
  | IShopifyCheckoutUpdatedEvent
  | IShopifyAbandonedCheckoutEvent
  | IShopifyOrderFulfilledEvent
  | IShopifyCheckoutStartedEvent
  | IShopifyAddToCartEvent
  | IShopifyRemoveFromCartEvent
  | IShopifyUpdateCartEvent
  | ISpinformResultEvent
  | ICouponCopiedEvent
  | IStartedCheckoutGKEvent
  | IMobileAddedGKEvent
  | IAddressSelectedGKEvent
  | IAddressCompletedGKEvent
  | IPaymentMethodSelectedGKEvent
  | IPaymentCompletedGKEvent
  | IOrderSuccessEvent
  | IOrderCompletedEvent
  | IPinCodeAddedGKEvent
  | IAddressAddedGKEvent
  | IKPMPPhoneNumberLoggedInEvent
  | IKPPhoneNumberLoggedInEvent
  | IKPMPShopifyLoggedInEvent
  | IKPMPSuccessfullyLoggedOutEvent
  | IKPWhatsAppLoggedInEvent
  | IKPMPTruecallerLoggedInEvent
  | IKPMPWhatsAppLoggedInEvent
  | IKPTruecallerLoggedInEvent
  | IKPShopifyLoggedInEvent
  | IKPSuccessfullyLoggedOutEvent;
